import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { MemberRoutingModule } from './member-routing.module';
import { MemberComponent } from './member.component';
import { HomeComponent } from './home/home.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { SettingsComponent } from './settings/settings.component';
import { ProfileComponent } from './settings/profile/profile.component';
import { SideNavComponent } from './settings/side-nav/side-nav.component';
import { ProfileEditComponent } from './settings/profile-edit/profile-edit.component';
import { ChangePasswordComponent } from './settings/change-password/change-password.component';
import { MemberLoaderComponent } from './member-loader/member-loader.component';
import { CreatespaceComponent } from './home/createspace/createspace.component';
import { SpaceListComponent } from './home/space-list/space-list.component';
import { EmptySpaceComponent } from './home/empty-space/empty-space.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from 'ng-pick-datetime';
import { OverlayLoaderComponent } from './overlay-loader/overlay-loader.component';
import { UpdateSpaceComponent } from './home/update-space/update-space.component';
import { SpacesComponent } from './spaces/spaces.component';
import { DeleteSpaceConfirmComponent } from './home/delete-space-confirm/delete-space-confirm.component';
import { EmptyShowcaseComponent } from './spaces/empty-showcase/empty-showcase.component';
import { SpaceSidebarComponent } from './spaces/space-sidebar/space-sidebar.component';
import { ShowcaseComponent } from './spaces/showcase/showcase.component';
import { TooltipDirective } from './../pipe/tooltip.directive';
import { SafeUrlMember } from './../pipe/safeurl-member.pipe';
import {  ClickOutsideDirective } from './../pipe/click-outside.directive';

import { TokenInterceptor } from './../services/token-interceptor';
import { CompanyInterceptor } from './../services/company-interceptor';

import { ShowcaseWorkshopComponent } from './spaces/showcase-workshop/showcase-workshop.component';
import { ImagesComponent } from './spaces/images/images.component';
import { FloorplanComponent } from './spaces/floorplan/floorplan.component';
import { MaterialComponent } from './spaces/material/material.component';
import { ResourceComponent } from './spaces/resource/resource.component';
import {
  PerfectScrollbarModule, PerfectScrollbarConfigInterface,
  PERFECT_SCROLLBAR_CONFIG
} from 'ngx-perfect-scrollbar';
import { VirtualToursComponent } from './spaces/virtual-tours/virtual-tours.component';
import { LoadingSpinnerComponent } from './loading-spinner/loading-spinner.component';
import { NgxBootstrapSliderModule } from 'ngx-bootstrap-slider';
import { TruncateMemberPipe } from '../pipe/truncate-member.pipe';
import { SharedModule } from './../shared.module';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { EditTwodimensionComponent } from './spaces/showcase/edit-twodimension/edit-twodimension.component';
import { VirtualToursTwodimensionComponent } from './spaces/showcase/virtual-tours-twodimension/virtual-tours-twodimension.component';
import { EmptyShowcaseTwodimensionComponent } from './spaces/showcase/empty-showcase-twodimension/empty-showcase-twodimension.component';
import { UploadImagesModalComponent } from './spaces/upload-images-modal/upload-images-modal.component';

// Request new dollhouse
import { RequestdollhouseGuard } from './../services/requestdollhouse/requestdollhouse-guard.service';
import { RequestdollhouseService } from './../services/requestdollhouse/requestdollhouse.service';

// Admin role guard
import { AdminGuard } from './../services/admin-guard.service';

import { DragDropDirective } from './../pipe/dragdrop.directive';
import { SingleDragDropDirective } from './../pipe/singledragdrop.directive';

// tslint:disable-next-line:max-line-length
import { EditTwodimensionSidebarComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/edit-twodimension-sidebar.component';
// tslint:disable-next-line:max-line-length
import { EditTwodimensionVirtualtourComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-virtualtour/edit-twodimension-virtualtour.component';
import { CreateHotspotsComponent } from './spaces/showcase/edit-twodimension/create-hotspots/create-hotspots.component';
import { CreateInfoHotspotsComponent } from './spaces/showcase/edit-twodimension/create-info-hotspots/create-info-hotspots.component';
import { DeleteHotspotsModalComponent } from './spaces/showcase/edit-twodimension/delete-hotspots-modal/delete-hotspots-modal.component';
import { PublicSpaceComponent } from './home/public-space/public-space.component';
import { PublicSpaceConfirmComponent } from './home/public-space-confirm/public-space-confirm.component';
import { StatisticsComponent } from './home/statistics/statistics.component';
import { BillingComponent } from './settings/account/billing/billing.component';
import { ManageComponent } from './settings/account/manage/manage.component';
import { BillingEditComponent } from './settings/account/billing-edit/billing-edit.component';
import { AddPaymentComponent } from './settings/account/add-payment/add-payment.component';
import { DeleteImageConfirmComponent } from './spaces/delete-image-confirm/delete-image-confirm.component';
import { EmptyFloorplanComponent } from './spaces/empty-floorplan/empty-floorplan.component';
import { FloorplanEditComponent } from './spaces/floorplan-edit/floorplan-edit.component';
import { FloorplanEditSidebarComponent } from './spaces/floorplan-edit-sidebar/floorplan-edit-sidebar.component';
import { DeleteFloorplanHotspotModalComponent } from './spaces/floorplan-edit/delete-floorplan-hotspot-modal/delete-floorplan-hotspot-modal.component';
import { ShopCameraComponent } from './settings/account/billing/shop-camera/shop-camera.component';
import { UnsubscribeCameraComponent } from './settings/account/billing/unsubscribe-camera/unsubscribe-camera.component';
import { OrientationModalComponent } from './spaces/showcase/edit-twodimension/orientation-modal/orientation-modal.component';
import { FloorplanImageEditComponent } from './spaces/floorplan-image-edit/floorplan-image-edit.component';
import { DeleteFloorplanComponent } from './spaces/delete-floorplan/delete-floorplan.component';
import { OwlModule } from 'ngx-owl-carousel';
import { SubscriptionRenewComponent } from './settings/subscription-renew/subscription-renew.component';
import { AmountSubscriptionComponent } from './settings/subscription-renew/amount-subscription/amount-subscription.component';
import { CardSubscriptionComponent } from './settings/subscription-renew/card-subscription/card-subscription.component';
import { ConfirmSubscriptionComponent } from './settings/subscription-renew/confirm-subscription/confirm-subscription.component';
import { VirtualToursVideoComponent } from './spaces/showcase/video/virtual-tours-video/virtual-tours-video.component';
import { EditVirtualToursVideoComponent } from './spaces/showcase/video/edit-virtual-tours-video/edit-virtual-tours-video.component';
import { UploadVideoModalComponent } from './spaces/showcase/video/upload-video-modal/upload-video-modal.component';
import { EmptyVideoShowcaseComponent } from './spaces/showcase/video/empty-video-showcase/empty-video-showcase.component';
import { MemberSpinnerComponent } from './member-spinner/member-spinner.component';
import { VideoListComponent } from './spaces/video-list/video-list.component';
import { AmountShowcaseComponent } from './spaces/showcase/amount-showcase/amount-showcase.component';
import { PlandetailsShowcaseComponent } from './spaces/showcase/amount-showcase/plandetails-showcase/plandetails-showcase.component';
import { MemberRequestLoaderComponent } from './member-request-loader/member-request-loader.component';
import { AddTitleComponent } from './spaces/showcase/video/add-title/add-title.component';
import { BufferLoaderComponent } from './buffer-loader/buffer-loader.component';
import { RequestdollhouseComponent } from './spaces/showcase/requestdollhouse/requestdollhouse.component';
import { UploaddollhouseComponent } from './spaces/showcase/requestdollhouse/uploaddollhouse/uploaddollhouse.component';
import { ConfirmdollhouseComponent } from './spaces/showcase/requestdollhouse/confirmdollhouse/confirmdollhouse.component';
import { AmountdollhouseComponent } from './spaces/showcase/requestdollhouse/amountdollhouse/amountdollhouse.component';
import { ImagesListComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/images-list/images-list.component';
import { HotspotListComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/hotspot-list/hotspot-list.component';
import { InfohotspotListComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/infohotspot-list/infohotspot-list.component';
import { SnapshotListComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/snapshot-list/snapshot-list.component';
import { SpaceSettingComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/space-setting/space-setting.component';
import { EditMatterTagComponent } from './spaces/showcase/edit-twodimension/edit-matter-tag/edit-matter-tag.component';
import { DeleteSnapshotModalComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/snapshot-list/delete-snapshot-modal/delete-snapshot-modal.component';
import { HighlightReelsCarouselComponent } from './spaces/showcase/edit-twodimension/highlight-reels-carousel/highlight-reels-carousel.component';
import { HighlightLoaderComponent } from './highlight-loader/highlight-loader.component';
import { StatisticsDetailComponent } from './spaces/statistics-detail/statistics-detail.component';
import { VirtualToursCubeComponent } from './spaces/showcase/virtual-tours-cube/virtual-tours-cube.component';
import { RoleComponent } from './settings/account/role/role.component';
import { InviteCollaboratorComponent } from './settings/account/invite-collaborator/invite-collaborator.component';
import { EditTitleModalComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/images-list/edit-title-modal/edit-title-modal.component';
import { DeleteTitleModalComponent } from './spaces/showcase/edit-twodimension/edit-twodimension-sidebar/images-list/delete-title-modal/delete-title-modal.component';
import { DatePipe } from '@angular/common';
import { CompanyResolverService } from '../services/company-resolver.service';
import { CollaboratorsComponent } from './home/collaborators/collaborators.component';
import { InviteCollaboratorsComponent } from './home/collaborators/invite-collaborators/invite-collaborators.component';
import { CollaboratorsListComponent } from './home/collaborators/collaborators-list/collaborators-list.component';
import { DeleteCollaboratorComponent } from './home/collaborators/collaborators-list/delete-collaborator/delete-collaborator.component';
import { ReactivateCollaboratorsComponent } from './home/collaborators/collaborators-list/reactivate-collaborators/reactivate-collaborators.component';
import { SpaceInviteCollaboratorComponent } from './home/space-invite-collaborator/space-invite-collaborator.component';
import { CollaboratorAccessComponent } from './settings/account/role/collaborator-access/collaborator-access.component';
import { RoleDeleteCollaboratorComponent } from './settings/account/role/role-delete-collaborator/role-delete-collaborator.component';
import { RoleReactivateCollaboratorComponent } from './settings/account/role/role-reactivate-collaborator/role-reactivate-collaborator.component';
import { CollaboratorEditComponent } from './home/collaborators/collaborator-edit/collaborator-edit.component';
import { SnapshotMemberLoaderComponent } from './snapshot-member-loader/snapshot-member-loader.component';
import { DeleteSnapshotComponent } from './spaces/showcase/edit-twodimension/delete-snapshot/delete-snapshot.component';
import { ShareSpaceComponent } from './home/share-space/share-space.component';
const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: true
};

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.languageUrl + '/', '.json');
}

@NgModule({
  declarations: [
    MemberComponent,
    HomeComponent,
    NotFoundComponent,
    SettingsComponent,
    ProfileComponent,
    ProfileEditComponent,
    SideNavComponent,
    ChangePasswordComponent,
    MemberLoaderComponent,
    CreatespaceComponent,
    SpaceListComponent,
    EmptySpaceComponent,
    OverlayLoaderComponent,
    UpdateSpaceComponent,
    SpacesComponent,
    DeleteSpaceConfirmComponent,
    EmptyShowcaseComponent,
    SpaceSidebarComponent,
    ShowcaseComponent,
    TooltipDirective,
    SafeUrlMember,
    ClickOutsideDirective,
    ShowcaseWorkshopComponent,
    ImagesComponent,
    FloorplanComponent,
    MaterialComponent,
    ResourceComponent,
    VirtualToursComponent,
    LoadingSpinnerComponent,
    EditTwodimensionComponent,
    VirtualToursTwodimensionComponent,
    EmptyShowcaseTwodimensionComponent,
    UploadImagesModalComponent,
    DragDropDirective,
    SingleDragDropDirective,
    EditTwodimensionSidebarComponent,
    EditTwodimensionVirtualtourComponent,
    CreateHotspotsComponent,
    CreateInfoHotspotsComponent,
    DeleteHotspotsModalComponent,
    PublicSpaceComponent,
    PublicSpaceConfirmComponent,
    StatisticsComponent,
    BillingComponent,
    ManageComponent,
    BillingEditComponent,
    AddPaymentComponent,
    DeleteImageConfirmComponent,
    EmptyFloorplanComponent,
    FloorplanEditComponent,
    FloorplanEditSidebarComponent,
    DeleteFloorplanHotspotModalComponent,
    ShopCameraComponent,
    UnsubscribeCameraComponent,
    OrientationModalComponent,
    FloorplanImageEditComponent,
    DeleteFloorplanComponent,
    SubscriptionRenewComponent,
    AmountSubscriptionComponent,
    CardSubscriptionComponent,
    ConfirmSubscriptionComponent,
    VirtualToursVideoComponent,
    EditVirtualToursVideoComponent,
    UploadVideoModalComponent,
    EmptyVideoShowcaseComponent,
    MemberSpinnerComponent,
    VideoListComponent,
    AmountShowcaseComponent,
    PlandetailsShowcaseComponent,
    MemberRequestLoaderComponent,
    AddTitleComponent,
    BufferLoaderComponent,
    RequestdollhouseComponent,
    UploaddollhouseComponent,
    ConfirmdollhouseComponent,
    AmountdollhouseComponent,
    TruncateMemberPipe,
    ImagesListComponent,
    HotspotListComponent,
    InfohotspotListComponent,
    SnapshotListComponent,
    SpaceSettingComponent,
    EditMatterTagComponent,
    DeleteSnapshotModalComponent,
    HighlightReelsCarouselComponent,
    HighlightLoaderComponent,
    StatisticsDetailComponent,
    RoleComponent,
    InviteCollaboratorComponent,
    VirtualToursCubeComponent,
    EditTitleModalComponent,
    DeleteTitleModalComponent,
    CollaboratorsComponent,
    InviteCollaboratorsComponent,
    CollaboratorsListComponent,
    DeleteCollaboratorComponent,
    ReactivateCollaboratorsComponent,
    SpaceInviteCollaboratorComponent,
    CollaboratorAccessComponent,
    RoleDeleteCollaboratorComponent,
    RoleReactivateCollaboratorComponent,
    CollaboratorEditComponent,
    SnapshotMemberLoaderComponent,
    DeleteSnapshotComponent,
    ShareSpaceComponent,
  ],
  imports: [
    BrowserModule,
    MemberRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgxPaginationModule,
    BrowserAnimationsModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    PerfectScrollbarModule,
    NgxBootstrapSliderModule,
    OwlModule,
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
      }
    }),
    ToastrModule.forRoot({
      positionClass: 'top-left',
      closeButton: true,
      timeOut: 20000,
      preventDuplicates: true,
    }),
    SharedModule
  ],
  providers: [
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CompanyInterceptor,
      multi: true
    },
    RequestdollhouseGuard,
    DatePipe,
    RequestdollhouseService,
    CompanyResolverService,
    AdminGuard
  ],
  bootstrap: [MemberComponent]
})
export class MemberModule {}
